import React, { useMemo } from 'react';
import { type MRT_PaginationState, type MRT_Updater, ShadcnReactTable } from '@talentmesh/table';
import { useTeamMembersContext } from '../Context/TeamMembersContext';
import { TeamMember } from '@/Models/TeamMember';
import TeamMemberRemoveDialog from './Components/TeamMemberRemoveDialog';
import TeamMemberInvitationRevokeDialog from './Components/TeamMemberInvitationRevokeDialog';
import createTeamMembersTableColumns from './Hooks/useTeamMembersTableColumns';
import NoTeamMembersOverlay from './Components/NoTeamMembersOverlay';
import useShadcnTable from '@/Hooks/useShadcnTable';
import PaginationConsts from '@/Utils/PaginationConsts';

export interface TeamMembersTableDialogs {
    removeMember?: TeamMember;
    revokeInvitee?: TeamMember;
}

const columns = createTeamMembersTableColumns();

const TeamMembersTable = () => {
    const { teamMembers, isOperationLoading, totalMembersCount, pageNumber, handlePaginationModelChange } =
        useTeamMembersContext();

    const pagination = useMemo(
        () => ({
            pageIndex: pageNumber,
            pageSize: PaginationConsts.regularPageSize,
        }),
        [pageNumber],
    );

    const table = useShadcnTable({
        columns,
        data: teamMembers,
        autoResetPageIndex: false,
        getRowId: (row) => row.id,
        enableRowSelection: false,
        enableColumnDragging: false,
        enableColumnOrdering: false,
        enableColumnPinning: false,
        enableColumnResizing: false,
        enableStickyHeader: false,
        enableTableFooter: false,
        manualFiltering: false,
        manualSorting: false,
        manualPagination: true,
        rowCount: totalMembersCount,
        enableBottomToolbar: true,
        enablePagination: true,
        mantinePaginationProps: { showRowsPerPage: false },
        state: {
            isLoading: isOperationLoading,
            pagination,
        },
        onPaginationChange: (updater: MRT_Updater<MRT_PaginationState>) => {
            const next = typeof updater === 'function' ? updater(pagination) : updater;
            handlePaginationModelChange({ page: next.pageIndex, pageSize: next.pageSize });
        },
        renderEmptyRowsFallback: () => <NoTeamMembersOverlay />,
        mantinePaperProps: { className: 'rounded-xl shadow-none' },
        mantineBottomToolbarProps: { className: 'shadow-none' },
    });

    return (
        <>
            <ShadcnReactTable table={table} />
            <TeamMemberInvitationRevokeDialog />
            <TeamMemberRemoveDialog />
        </>
    );
};

export default TeamMembersTable;
